import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, 
  Briefcase, 
  Users, 
  Building2, 
  ArrowRight, 
  CheckCircle2,
  Phone,
  Clock,
  Award,
  Target,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCmsContent } from '../hooks/useCmsContent';

const FALLBACK_ACCUEIL = {
  hero: { badge: 'Groupe AERH Polynésie', title: 'Révélons ensemble le', highlight: 'potentiel polynésien', description: 'Formation, consulting et recrutement au service du développement des talents et des entreprises de Polynésie française.', cta_primary: 'Découvrir nos formations', cta_secondary: 'Nous contacter' },
  stats: { items: [{ value: '5', label: "Métiers d'expertise" }, { value: '500+', label: 'Clients accompagnés' }, { value: '98%', label: 'Taux de satisfaction' }, { value: '10+', label: "Années d'expérience" }] },
  services: { eyebrow: 'Nos Activités', title: 'Des solutions adaptées à vos besoins', description: 'Le Groupe AERH Polynésie vous accompagne dans le développement de vos compétences et de votre entreprise avec des services sur mesure.' },
  about: { eyebrow: 'Qui sommes-nous', title: 'Une équipe passionnée au service de la Polynésie', paragraph1: "Fondé par Eric ASPINAS, le Groupe AERH Polynésie est né de la conviction qu'il faut donner confiance, révéler les potentiels et ouvrir de nouvelles perspectives pour les talents cachés.", paragraph2: "Notre équipe intergénérationnelle est animée par des valeurs fortes : le respect, l'entraide, la rigueur et le sens de la communauté.", values: [{ title: 'Respect', description: 'Une approche humaine et bienveillante' }, { title: 'Entraide', description: 'La solidarité au cœur de notre démarche' }, { title: 'Rigueur', description: 'Un travail de qualité professionnelle' }, { title: 'Communauté', description: 'Au service du développement local' }] },
  cta: { title: 'Prêt à développer vos compétences ?', description: "Contactez-nous pour discuter de vos besoins en formation, consulting ou recrutement. Notre équipe est à votre écoute.", phone: '+689 87 73 78 15' },
  testimonials: { eyebrow: 'Témoignages', title: 'Ils nous font confiance', items: [] },
};

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1741454762273-ffe379671955?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxUYWhpdGklMjBvZmZpY2UlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzczNTE2OTgyfDA&ixlib=rb-4.1.0&q=85&w=1920",
  training: "https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/4cwn6uf7_20230922_093644.jpg",
  consulting: "https://images.unsplash.com/photo-1752159684779-0639174cdfac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzczNTE2OTkxfDA&ixlib=rb-4.1.0&q=85",
  recruitment: "https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/u0me3lk2_141209352_275139740693455_6138775839380989747_o.jpg",
  moanaPaye: "https://images.unsplash.com/photo-1753955900083-b62ee8d97805?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxwYXlyb2xsJTIwYWNjb3VudGluZyUyMGRvY3VtZW50cyUyMGNhbGN1bGF0b3IlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc1NDkyOTU2fDA&ixlib=rb-4.1.0&q=85",
  room: "https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/3i9mb6f8_20230524_153525.jpg",
  team: "https://images.unsplash.com/photo-1758520144424-2e04f4ad7f60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxjYXVjYXNpYW4lMjBidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwb2ZmaWNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NTQ5MzQxNXww&ixlib=rb-4.1.0&q=85"
};

const TEAM_MEMBERS = [
  { name: 'Eric ASPINAS', role: 'Fondateur & Directeur', image: 'https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/k8sw905t_Eric.jpeg' },
  { name: 'Julie', role: 'Responsable Formation', image: 'https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/t3qe4q2w_Julie.jpeg' },
  { name: 'Kealii', role: 'Chargé de Mission', image: 'https://customer-assets.emergentagent.com/job_dbe93cb4-9c50-4fba-b93b-913745c122fd/artifacts/5cpzi4ou_Kealii.jpeg' },
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const services = [
  {
    icon: GraduationCap,
    title: 'Formations',
    description: 'Des formations professionnelles adaptées aux réalités polynésiennes. E-learning, présentiel et bilan de compétences.',
    link: '/formations',
    color: 'bg-teal-50 text-teal-700',
    image: IMAGES.training
  },
  {
    icon: Briefcase,
    title: 'Consulting',
    description: 'Accompagnement en création d\'entreprise, gestion RH, rédaction de business plan et audit.',
    link: '/consulting',
    color: 'bg-gold-50 text-gold-700',
    image: IMAGES.consulting
  },
  {
    icon: Users,
    title: 'Recrutement',
    description: 'Identification, sélection et intégration de talents durables. 100% local, 100% sur mesure.',
    link: '/recrutement',
    color: 'bg-teal-50 text-teal-700',
    image: IMAGES.recruitment
  },
  {
    icon: CheckCircle2,
    title: 'Moana Paye',
    description: 'Externalisation de la paie en Polynésie française. Bulletins conformes CPS, CST, MO inclus.',
    link: '/moana-paye',
    color: 'bg-gold-50 text-gold-700',
    image: IMAGES.moanaPaye
  },
  {
    icon: Building2,
    title: 'Location de salles',
    description: 'Salles de réunion et formation équipées au cœur de Papeete. Climatisation, Wi-Fi, vidéoprojecteur.',
    link: '/location-salles',
    color: 'bg-teal-50 text-teal-700',
    image: IMAGES.room
  }
];

const TeamCarousel = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % TEAM_MEMBERS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const prev = () => setCurrent(c => (c - 1 + TEAM_MEMBERS.length) % TEAM_MEMBERS.length);
  const next = () => setCurrent(c => (c + 1) % TEAM_MEMBERS.length);

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      className="relative"
      data-testid="team-carousel"
    >
      <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[3/4]">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={TEAM_MEMBERS[current].image}
            alt={TEAM_MEMBERS[current].name}
            className="w-full h-full object-cover object-top"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.5 }}
          />
        </AnimatePresence>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-white font-bold text-xl">{TEAM_MEMBERS[current].name}</p>
              <p className="text-white/80 text-sm">{TEAM_MEMBERS[current].role}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <button
          onClick={prev}
          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-2 transition-all"
          data-testid="carousel-prev"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={next}
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-2 transition-all"
          data-testid="carousel-next"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>
      <div className="flex justify-center gap-2 mt-4">
        {TEAM_MEMBERS.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-teal-600 w-6' : 'bg-slate-300'}`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
      <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-gold-500 rounded-2xl -z-10" />
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-teal-100 rounded-full -z-10" />
    </motion.div>
  );
};

export const HomePage = () => {
  const cms = useCmsContent('accueil', FALLBACK_ACCUEIL);
  const cmsStats = cms.stats?.items || FALLBACK_ACCUEIL.stats.items;
  const cmsValues = cms.about?.values || FALLBACK_ACCUEIL.about.values;
  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.hero} 
            alt="Tahiti paysage" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 via-teal-900/50 to-teal-900/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20">
          <motion.div 
            className="max-w-2xl"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block bg-gold-500/20 text-gold-400 px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              {cms.hero?.badge || FALLBACK_ACCUEIL.hero.badge}
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4"
            >
              Cabinet RH à Tahiti : Formation, consulting, recrutement et solutions paie au service du développement des talents et des entreprises de Polynésie française.
            </motion.h1>
            <motion.h2
              variants={fadeInUp}
              className="text-xl md:text-2xl font-semibold text-gold-400 mb-6 italic"
            >
              Révélons ensemble le potentiel polynésien
            </motion.h2>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap gap-4"
            >
              <Button 
                asChild
                className="bg-gold-500 hover:bg-gold-600 text-slate-900 font-semibold rounded-full px-8 py-6 text-base shadow-xl hover:shadow-2xl transition-all"
                data-testid="hero-cta-discover"
              >
                <a href="#services" onClick={(e) => { e.preventDefault(); document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Découvrir nos services
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-700 rounded-full px-8 py-6 text-base"
                data-testid="hero-cta-contact"
              >
                <Link to="/contact">
                  Nous contacter
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {cmsStats.map((stat, index) => {
              const icons = [Briefcase, Users, Award, Clock];
              const Icon = icons[index] || CheckCircle2;
              return (
              <motion.div 
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-teal-100 text-teal-700 mb-4">
                  <Icon className="w-7 h-7" />
                </div>
                <div className="text-4xl font-bold text-teal-700 mb-1">{stat.value}</div>
                <div className="text-slate-600">{stat.label}</div>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold-600 font-medium uppercase tracking-wider text-sm">{cms.services?.eyebrow || 'Nos Activités'}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              {cms.services?.title || 'Des solutions adaptées à vos besoins'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {cms.services?.description || 'Le Groupe AERH Polynésie vous accompagne dans le développement de vos compétences et de votre entreprise avec des services sur mesure.'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-100"
                data-testid={`service-card-${service.title.toLowerCase()}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <div className={`absolute top-4 left-4 w-12 h-12 rounded-xl ${service.color} flex items-center justify-center shadow-lg`}>
                    <service.icon className="w-6 h-6" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="inline-flex items-center bg-teal-700 text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-gold-600 transition-colors"
                  >
                    En savoir plus
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-16 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold-600 font-medium uppercase tracking-wider text-sm">{cms.about?.eyebrow || 'Qui sommes-nous'}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                {cms.about?.title || 'Une équipe passionnée au service de la Polynésie'}
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                {cms.about?.paragraph1 || "Fondé par Eric ASPINAS, le Groupe AERH Polynésie est né de la conviction qu'il faut donner confiance, révéler les potentiels et ouvrir de nouvelles perspectives pour les talents cachés."}
              </p>
              <p className="text-slate-600 mb-8">
                {cms.about?.paragraph2 || "Notre équipe intergénérationnelle est animée par des valeurs fortes : le respect, l'entraide, la rigueur et le sens de la communauté."}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {cmsValues.map((value) => (
                  <div key={value.title} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-900">{value.title}</div>
                      <div className="text-sm text-slate-600">{value.description}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                asChild
                className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 py-6"
                data-testid="about-cta"
              >
                <Link to="/a-propos">
                  Découvrir notre histoire
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </motion.div>

            <TeamCarousel />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-700" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {cms.cta?.title || 'Prêt à développer votre entreprise ?'}
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              {cms.cta?.description || "Formation, consulting, recrutement, paie ou location de salles — notre équipe est à votre écoute pour vous accompagner."}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                asChild
                className="bg-gold-500 hover:bg-gold-600 text-slate-900 font-semibold rounded-full px-8 py-6 text-base shadow-xl"
                data-testid="cta-contact-bottom"
              >
                <Link to="/contact">
                  <Phone className="mr-2 w-5 h-5" />
                  Nous contacter
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-700 rounded-full px-8 py-6 text-base"
              >
                <a href="tel:+68987737815">
                  {cms.cta?.phone || '+689 87 73 78 15'}
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials removed - waiting for real testimonials */}
    </div>
  );
};

export default HomePage;
